import { PaginatedTable, PaginationOrSortParameters } from '@act/storybook';
import { useEffect, useState } from 'react';
import { hospitalizationsActions } from 'redux/slices/hospitalizations.slice';
import { membersAppSelectors } from 'redux/slices/memberSlice';
import { userAppSelectors } from 'redux/slices/usersSlice';
import { HOSPITALIZATION_TABLE_COLUMN_CONFIGURATIONS } from './hospitalizationTableConfigurations';
import { useApiCall } from 'redux/utils/api-hook';
import { hospitalizationsRequests } from 'redux/requests/hospitalizations.requests';
import { HospitalizationDto } from '@act/shared/data-transfer-objects';
import { useDispatch } from 'react-redux';
import { useAppNavigation } from 'react-components/app/routing/routes';

const HospitalizationsTable = ({
  specialFilter,
  clientIds,
  guideIds,
  statusIds,
}: {
  specialFilter: 'not-attempted' | 'not-offered' | null;
  clientIds: number[];
  guideIds: string[];
  statusIds: number[];
}) => {
  const navigate = useAppNavigation();
  const dispatch = useDispatch();

  const [tableStatus, setTableStatus] = useState<
    'initial' | 'loading' | 'success' | 'failed'
  >('initial');
  const [hospitalizations, setHospitalizations] = useState<
    HospitalizationDto[]
  >([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  // Setting up state for sorting
  const [bounds, setBounds] = useState<PaginationOrSortParameters>({
    page: 0,
    rowsPerPage: 10,
    property: 'dischargeDate',
    sortOrder: 'desc',
  });

  const memberMap = membersAppSelectors.useSelectMembersMap();
  const guideMap = userAppSelectors.useSelectCurrentGuidesMap();

  const [
    hospitalizationResponse,
    loadingHospitalizations,
    getHospitalizations,
  ] = useApiCall(hospitalizationsRequests.getHospitalizations);

  useEffect(() => {
    if (hospitalizationResponse) {
      setTableStatus('success');
      dispatch(
        hospitalizationsActions.gotHospitalizations(hospitalizationResponse)
      );

      setHospitalizations(hospitalizationResponse.data);
      setTotalRecords(hospitalizationResponse.pagination.totalItems);
    }
  }, [hospitalizationResponse]);

  useEffect(() => {
    setTableStatus('loading');
    makeRequest();
  }, [specialFilter]);

  useEffect(() => {
    if (!loadingHospitalizations) {
      makeRequest();
    }
  }, [clientIds, guideIds, statusIds, bounds]);

  const makeRequest = () => {
    const params = {
      'page-start': bounds.page * bounds.rowsPerPage,
      'page-size': bounds.rowsPerPage,
      'sort-by': bounds.property,
      'sort-direction': bounds.sortOrder,
      'special-filter': specialFilter,
      clientIds: clientIds,
      guideIds: guideIds,
      statusIds: statusIds,
    };
    getHospitalizations({ params });
  };

  const handlePaginationOrSort = (parameters: PaginationOrSortParameters) => {
    const newBounds = {
      ...bounds,
      ...parameters,
    };
    setBounds(newBounds);
    setTableStatus('loading');
  };

  return (
    <PaginatedTable
      status={tableStatus}
      inMemoryPagination={false}
      data={hospitalizations.map(hospitalization => ({
        ...hospitalization,
        memberStatus: memberMap[hospitalization.memberId]?.memberStatus,
        memberName: memberMap[hospitalization.memberId]?.name,
        assignedGuide: guideMap[hospitalization.member.assignedGuideId]?.name,
      }))}
      onRowClick={(hospitalization: HospitalizationDto) => {
        navigate.toMemberPage(
          hospitalization?.member.assignedGuideId,
          hospitalization?.member.id
        );
      }}
      page={bounds.page ?? 0}
      rowsPerPage={bounds.rowsPerPage ?? 10}
      rowsPerPageOptions={[5, 10, 25]}
      count={totalRecords || 0}
      columnConfig={HOSPITALIZATION_TABLE_COLUMN_CONFIGURATIONS}
      onPaginationOrSort={handlePaginationOrSort}
      initialSortBy="dischargeDate"
      initialSortDirection="desc"
    />
  );
};

export default HospitalizationsTable;
